// HUB-8234: shared mandatory weighing scan response handling (mobile/web contract)
// HubOps Web uses vue-toastification ($toast) — there is no separate snackbar component.
// Soft-block: full weighingMessage for toast; AWB unchanged — use the field the scan API already returned (or scanned AWB + getHubColourCode on inscan).

export const MANDATORY_WEIGHING_BLOCK_CODES = {
  manualShipmentInScan: 121,
  bagInShipment: 121,
  bagOutProcess: 109,
  getShipmentDestinationForSecondarySort: 106,
  getShipmentHubDestination: 105,
  secondarySortScanAWBinBarcodeForBagging: 121,
  getShipmentDetailsForRtoDtoSlip: 121
};

export const MANDATORY_WEIGHING_UI_MESSAGE = 'Weighing Required on DWS/Sorter before proceeding';

export const MANDATORY_WEIGHING_ACTION_WARNING = 1;
export const MANDATORY_WEIGHING_ACTION_SOFT_BLOCK = 2;

export const MANDATORY_WEIGHING_ACTION_OPTIONS = [
  { value: 'warning', label: 'Warning' },
  { value: 'softblock', label: 'Soft Block' },
  { value: 'hardblock', label: 'Hard Block' }
];

export const MANDATORY_WEIGHING_EVENT_LABELS = {
  manifestInscan: 'Manifest Inscan',
  bagOut: 'Bag Out',
  printLink: 'Print RTO / DTO Slip',
  secondarySort: 'Secondary Sort',
  bagIn: 'Bag In',
  d2bBagIn: 'D2B Bag In'
};

const TOAST_OPTIONS = {
  timeout: 8000,
  position: 'top-right',
  closeOnClick: true,
  pauseOnHover: true
};

const HARD_BLOCK_ALERT_TITLE = 'Mandatory Weighing';

const HARD_BLOCK_CONFIRM_TOAST_STYLE = {
  background: '#ffc107',
  color: '#212529',
  confirmButtonColor: '#212529',
  confirmButtonText: 'OK'
};

function showMandatoryWeighingHardBlockError(vm, message) {
  if (vm.$alertify && vm.$alertify.error) {
    vm.$alertify.error(message);
    return;
  }
  if (vm.$toast && vm.$toast.error) {
    vm.$toast.error(message, TOAST_OPTIONS);
  }
}

function showMandatoryWeighingHardBlockConfirmAlert(vm, message) {
  const displayMessage = message || MANDATORY_WEIGHING_UI_MESSAGE;


  if (vm.$fire) {
    vm.$fire({
      title: HARD_BLOCK_ALERT_TITLE,
      text: displayMessage,
      icon: 'warning',
      allowOutsideClick: false,
      allowEscapeKey: true,
      background: HARD_BLOCK_CONFIRM_TOAST_STYLE.background,
      color: HARD_BLOCK_CONFIRM_TOAST_STYLE.color,
      confirmButtonColor: HARD_BLOCK_CONFIRM_TOAST_STYLE.confirmButtonColor,
      confirmButtonText: HARD_BLOCK_CONFIRM_TOAST_STYLE.confirmButtonText
    })
    return;
  }

  if (vm.$alert) {
    vm.$alert(displayMessage, HARD_BLOCK_ALERT_TITLE, 'warning', {
      confirmButtonText: 'OK',
      allowOutsideClick: false
    });
    return;
  }

  if (vm.$alertify && vm.$alertify.alertWithTitle) {
    vm.$alertify.alertWithTitle(HARD_BLOCK_ALERT_TITLE, displayMessage );
    return;
  }

  if (typeof window !== 'undefined' && window.alert) {
    window.alert(`${HARD_BLOCK_ALERT_TITLE}\n\n${displayMessage}`);
  }
 
}

export function getMandatoryWeighingBlockCode(endpointKey) {
  return MANDATORY_WEIGHING_BLOCK_CODES[endpointKey] || null;
}

export function isMandatoryWeighingHardBlock(resultCode, endpointKey) {
  const blockCode = getMandatoryWeighingBlockCode(endpointKey);
  return blockCode != null && Number(resultCode) === blockCode;
}

export function isMandatoryWeighingSoftBlock(data) {
  return data && Number(data.weighingAction) === MANDATORY_WEIGHING_ACTION_SOFT_BLOCK;
}

/** When weighingAction=2, hide null/0/empty in Web UI; API still sends null/0 keys. */
export function shouldShowMandatoryWeighingResultField(isSoftBlock, value) {
  if (!isSoftBlock) {
    return true;
  }
  return shouldDisplayMandatoryWeighingResultField(value);
}

/** HUB-8234: null / 0 / empty — hide on soft-block screens (Web). */
export function shouldDisplayMandatoryWeighingResultField(value) {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'number' && value === 0) {
    return false;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return false;
  }
  return true;
}

function showMandatoryWeighingToast(vm, message, type) {
  if (vm.$toast) {
    if (type === 'warning' && vm.$toast.warning) {
      vm.$toast.warning(message, TOAST_OPTIONS);
      return;
    }
    if (vm.$toast.info) {
      vm.$toast.info(message, TOAST_OPTIONS);
      return;
    }
  }
  if (vm.$alertify) {
    if (type === 'warning' && vm.$alertify.warning) {
      vm.$alertify.warning(message);
      return;
    }
    if (vm.$alertify.error) {
      vm.$alertify.error(message);
    }
  }
}

/**
 * Show mandatory weighing warning or soft-block toast (full weighingMessage from API).
 */
export function showMandatoryWeighingSnackbar(vm, data) {
  if (!data || data.weighingAction === undefined) {
    return;
  }

  const action = Number(data.weighingAction);
  if (action === 0) {
    return;
  }

  if (action === MANDATORY_WEIGHING_ACTION_WARNING) {
    showMandatoryWeighingToast(
      vm,
      data.weighingMessage || MANDATORY_WEIGHING_UI_MESSAGE,
      'warning'
    );
    return;
  }

  if (action === MANDATORY_WEIGHING_ACTION_SOFT_BLOCK && data.weighingMessage) {
    showMandatoryWeighingToast(vm, data.weighingMessage, 'warning');
  }
}

export function handleMandatoryWeighingHardBlock(vm, data, endpointKey) {
  if (!isMandatoryWeighingHardBlock(data && data.ResultCode, endpointKey)) {
    return false;
  }
  const message = (data && data.ReturnMessage) || MANDATORY_WEIGHING_UI_MESSAGE;
  showMandatoryWeighingHardBlockConfirmAlert(vm, message);
  return true;
}



// WEBPACK FOOTER //
// ./src/utils/mandatoryWeighingResponse.js